import { useEffect, useState } from "react";
import { MapContainer as LeafletMap, TileLayer, Marker, Popup, ZoomControl, useMap } from "react-leaflet";
import { Icon, LatLngBounds } from "leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2 } from "lucide-react";
import { ExternalLink } from "lucide-react";
import { Link } from "@inertiajs/react";
import { Badge } from "./ui/badge";

// Création d'icônes personnalisées pour la carte
const createCustomIcon = (certified: boolean) => {
  return new Icon({
    iconUrl: certified 
      ? "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png"
      : "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });
};

// Style personnalisé pour les popups
const customPopupStyle = `
  .leaflet-popup-content-wrapper {
    border-radius: 8px;
    padding: 0;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  .leaflet-popup-content {
    margin: 0;
    padding: 0;
    width: 260px !important;
  }
  .leaflet-popup-tip {
    background-color: white;
  }
`;

// Coordonnées pour centrer sur la France
const FRANCE_CENTER: [number, number] = [46.603354, 1.888334]; // Centre approximatif de la France
const FRANCE_ZOOM = 6; // Niveau de zoom pour voir la France entière
const CITY_ZOOM = 13; // Niveau de zoom pour une ville

// Composant pour mettre à jour la vue de la carte
const MapUpdater = ({ center, zoom, bounds }: { center: [number, number], zoom: number, bounds?: LatLngBounds }) => {
  const map = useMap();
  
  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds);
    } else {
      map.setView(center, zoom);
    }
  }, [map, center, zoom, bounds]);
  
  return null;
};

interface Physio {
  id: string;
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  lat: number;
  lng: number;
  specialties: string[];
  isCertified: boolean;
  phone: string;
  profileType: "basic" | "filled" | "certified";
  source?: "platform" | "rpps";
}

interface MapContainerProps {
  physios: Physio[];
  cityFilter?: string;
  searchCoordinates?: [number, number];
}

const MapContainer = ({ physios, cityFilter, searchCoordinates }: MapContainerProps) => {
  const [isLoading, setIsLoading] = useState(true);
  
  // Inject custom popup styles
  useEffect(() => {
    const styleTag = document.createElement("style");
    styleTag.innerHTML = customPopupStyle;
    document.head.appendChild(styleTag);
    
    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);
  
  // Simulate loading (can be removed in production)
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  // Déterminer si on a besoin d'afficher la France entière
  const shouldShowFullFrance = () => {
    return !cityFilter && !searchCoordinates && physios.length > 1;
  };

  // Calculate center of the map
  const getMapCenter = () => {
    // Si pas de physios, centrer sur les coordonnées de recherche si disponibles
    if (physios.length === 0) {
      return searchCoordinates || FRANCE_CENTER;
    }

    const validPhysios = physios.filter(p => p.lat && p.lng);
    if (validPhysios.length === 0) {
      return searchCoordinates || FRANCE_CENTER;
    }

    // Si recherche par ville, centre sur le premier résultat de cette ville
    if (cityFilter) {
      const cityPhysios = validPhysios.filter(p =>
        p.city.toLowerCase().includes(cityFilter.toLowerCase())
      );

      if (cityPhysios.length > 0) {
        return [cityPhysios[0].lat, cityPhysios[0].lng];
      }
    }

    // Si on a des coordonnées de recherche, les utiliser pour centrer
    if (searchCoordinates) {
      return searchCoordinates;
    }

    // Si on doit afficher la France entière, on centre sur la France
    if (shouldShowFullFrance()) {
      return FRANCE_CENTER;
    }

    // Calculate average latitude and longitude for center
    const sumLat = validPhysios.reduce((sum, p) => sum + p.lat, 0);
    const sumLng = validPhysios.reduce((sum, p) => sum + p.lng, 0);
    return [sumLat / validPhysios.length, sumLng / validPhysios.length];
  };

  // Get zoom level based on whether we are showing all of France or a city
  const getZoomLevel = () => {
    if (cityFilter || searchCoordinates) return CITY_ZOOM;
    return shouldShowFullFrance() ? FRANCE_ZOOM : CITY_ZOOM;
  };

  // Calculate bounds for all markers to fit within the view
  const getBounds = () => {
    // Si on a des coordonnées de recherche, on laisse setView(center, zoom) gérer
    if (searchCoordinates) {
      return undefined;
    }

    // Si on doit afficher la France entière, on ne définit pas de bounds
    if (shouldShowFullFrance()) {
      return undefined;
    }

    // S'il n'y a pas ou peu de points, on ne définit pas de bounds
    if (physios.length <= 1) return undefined;
    
    const validPhysios = physios.filter(p => p.lat && p.lng);
    if (validPhysios.length <= 1) return undefined;
    
    // Si recherche par ville, limiter les bounds aux résultats de cette ville
    let physiosToUse = validPhysios;
    if (cityFilter) {
      const cityPhysios = validPhysios.filter(p => 
        p.city.toLowerCase().includes(cityFilter.toLowerCase())
      );
      
      if (cityPhysios.length > 0) {
        physiosToUse = cityPhysios;
      }
    }
    
    const bounds = new LatLngBounds(
      [physiosToUse[0].lat, physiosToUse[0].lng],
      [physiosToUse[0].lat, physiosToUse[0].lng]
    );
    
    physiosToUse.forEach(physio => {
      bounds.extend([physio.lat, physio.lng]);
    });
    
    return bounds;
  };

  const mapCenter = getMapCenter() as [number, number];
  const zoomLevel = getZoomLevel();
  const mapBounds = getBounds();

  return (
    <div className="relative overflow-hidden h-full">
      {isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
          <div className="flex flex-col items-center">
            <Loader2 className="h-10 w-10 animate-spin text-green-600" />
            <span className="mt-2 text-sm text-gray-600 font-medium">Chargement de la carte...</span>
          </div>
        </div>
      ) : (
        <LeafletMap 
          center={mapCenter} 
          zoom={zoomLevel} 
          style={{ height: "100%", width: "100%" }}
          className="z-0"
          zoomControl={false} // Désactiver le contrôle de zoom par défaut
        >
          <MapUpdater center={mapCenter} zoom={zoomLevel} bounds={mapBounds} />
          <ZoomControl position="bottomright" /> {/* Repositionner le contrôle de zoom */}
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          
          {physios.filter(physio => physio.lat != null && physio.lng != null && physio.source !== "rpps").map((physio) => (
            <Marker 
              key={physio.id} 
              position={[physio.lat, physio.lng]}
              icon={createCustomIcon(physio.isCertified)}
            >
              <Popup className="custom-popup" closeButton={false}>
                <div>
                  <div className="bg-gray-50 p-3 border-b">
                    <div className="flex justify-between items-start">
                      <h3 className="font-medium text-gray-900">
                        {physio.firstName} {physio.lastName}
                      </h3>
                      {physio.isCertified && (
                        <Badge className="bg-green-500 hover:bg-green-600 rounded-full text-[10px] py-0.5 px-2">
                          Approuvé
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{physio.address}</p>
                    <p className="text-xs text-gray-500">{physio.city}</p>
                  </div>
                  
                  <div className="p-3">
                    {physio.specialties && physio.specialties.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {physio.specialties.slice(0, 3).map((specialty, index) => (
                          <Badge 
                            key={index} 
                            variant="secondary" 
                            className="text-xs font-normal bg-gray-100 text-gray-700 hover:bg-gray-100 px-2 py-0.5"
                          >
                            {specialty}
                          </Badge>
                        ))}
                        {physio.specialties.length > 3 && (
                          <Badge 
                            variant="secondary" 
                            className="text-xs font-normal bg-gray-100 text-gray-700 hover:bg-gray-100 px-2 py-0.5"
                          >
                            +{physio.specialties.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}
                    
                    <Link 
                      href={`/physiotherapists/${physio.id}`}
                      className="flex items-center justify-center w-full px-3 py-2 bg-green-500 text-white text-sm rounded-md hover:bg-green-600 transition-colors"
                      method="get"
                    >
                      Voir le profil
                      <ExternalLink className="ml-1 h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </LeafletMap>
      )}
    </div>
  );
};

export default MapContainer; 
